<template>
  <div class="rpt-order-forecast">
    <div v-if="loading" class="report-loading">
      <i class="el-icon-loading"></i> 数据加载中...
    </div>
    <div v-else class="report-shell">
      <header class="report-header">
        <div class="header-row">
          <div>
            <h1>{{ title }}</h1>
            <p class="subtitle">{{ subtitle }}</p>
          </div>
          <div class="header-actions">
            <span class="report-badge"><i class="fa fa-clock-o"></i> 更新至 {{ updateDate }}</span>
          </div>
        </div>
      </header>

      <section class="kpi-grid">
        <article v-for="(kpi, i) in kpis" :key="i" class="kpi-card">
          <div class="kpi-label">{{ kpi.label }}</div>
          <div class="kpi-value" :style="{ color: toneColor(kpi.tone) }">{{ kpi.value }}</div>
          <div class="kpi-note">{{ kpi.note }}</div>
        </article>
      </section>

      <section class="table-card">
        <div class="table-card-head">
          <div>
            <div class="section-title"><i class="fa fa-table"></i><span>{{ tableTitle }}</span></div>
            <div class="section-note">预测值单位：万元；完成率对应各周期经营目标；增幅为同比口径。</div>
          </div>
          <div class="legend">
            <span><i class="legend-dot" style="background:#0aa66a"></i>达成或正增长</span>
            <span><i class="legend-dot" style="background:#d98213"></i>轻度偏差</span>
            <span><i class="legend-dot" style="background:#e54b3d"></i>重点风险</span>
          </div>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th rowspan="2" class="col-name">链群</th>
                <th v-for="p in displayPeriods" :key="'h-'+p.key" colspan="3">{{ p.label }}</th>
              </tr>
              <tr>
                <template v-for="p in displayPeriods">
                  <th :key="'s1-'+p.key">预测</th>
                  <th :key="'s2-'+p.key">{{ p.isAnnual ? '保障度' : '完成率' }}</th>
                  <th :key="'s3-'+p.key">增幅</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr class="total-row">
                <td>卡奥斯</td>
                <template v-for="p in displayPeriods">
                  <td :key="'t-'+p.key" class="forecast-cell">{{ num(totalRow[p.key] ? totalRow[p.key][0] : null) }}</td>
                 <td :key="'tr-'+p.key" class="rate-cell">
                    <template v-if="totalRow[p.key] && totalRow[p.key][1] !== null">
                     <span class="rate-fill" :style="{width: Math.min(totalRow[p.key][1], 100) + '%', background: rateColor(totalRow[p.key][1], 'completion')}"></span>
                     <span class="rate-text" :style="{color: rateColor(totalRow[p.key][1], 'completion')}">{{ totalRow[p.key][1].toFixed(1) }}%</span>
                    </template>
                    <span v-else class="rate-text" style="color:#94a3b8">—</span>
                  </td>
                  <td :key="'tg-'+p.key" class="rate-cell">
                    <template v-if="totalRow[p.key] && totalRow[p.key][2] !== null">
                      <span class="rate-fill" :style="{width: Math.min(Math.abs(totalRow[p.key][2]) * 3, 100) + '%', background: rateColor(totalRow[p.key][2], 'growth')}"></span>
                      <span class="rate-text" :style="{color: rateColor(totalRow[p.key][2], 'growth')}">{{ totalRow[p.key][2] >= 0 ? '+' : '' }}{{ totalRow[p.key][2].toFixed(1) }}%</span>
                    </template>
                    <span v-else class="rate-text" style="color:#94a3b8">—</span>
                  </td>
                </template>
              </tr>
              <tr v-for="(row, ri) in rows" :key="ri">
                <td>{{ row.name }}</td>
                <template v-for="p in displayPeriods">
                  <td :key="'v-'+p.key+'-'+ri" class="forecast-cell">{{ num(row[p.key] ? row[p.key][0] : null) }}</td>
                 <td :key="'vr-'+p.key+'-'+ri" class="rate-cell">
                    <template v-if="row[p.key] && row[p.key][1] !== null">
                     <span class="rate-fill" :style="{width: Math.min(row[p.key][1], 100) + '%', background: rateColor(row[p.key][1], 'completion')}"></span>
                     <span class="rate-text" :style="{color: rateColor(row[p.key][1], 'completion')}">{{ row[p.key][1].toFixed(1) }}%</span>
                    </template>
                    <span v-else class="rate-text" style="color:#94a3b8">—</span>
                  </td>
                  <td :key="'vg-'+p.key+'-'+ri" class="rate-cell">
                    <template v-if="row[p.key] && row[p.key][2] !== null">
                      <span class="rate-fill" :style="{width: Math.min(Math.abs(row[p.key][2]) * 3, 100) + '%', background: rateColor(row[p.key][2], 'growth')}"></span>
                      <span class="rate-text" :style="{color: rateColor(row[p.key][2], 'growth')}">{{ row[p.key][2] >= 0 ? '+' : '' }}{{ row[p.key][2].toFixed(1) }}%</span>
                    </template>
                    <span v-else class="rate-text" style="color:#94a3b8">—</span>
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="report-foot">
          <span>数据来源：CRM在手订单、销售漏斗、项目交付计划、财务年度预算。</span>
          <span>口径说明：管理预测口径，示例数据已按链群经营逻辑补齐，不作为财务披露依据。</span>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

// 根据当前日期计算动态周期：当前月 + 当前及后续季度 + 年度
function getDynamicPeriods() {
  var now = new Date()
  var month = now.getMonth() + 1
  var currentMonth = String(month).padStart(2, '0')
  var currentQNum = Math.ceil(month / 3)

  var periods = []
  // 当前月
  periods.push({
    type: 'MONTH',
    code: currentMonth,
    label: month + '月',
    key: 'month',
    isAnnual: false
  })
  // 当前及后续季度
  for (var q = currentQNum; q <= 4; q++) {
    periods.push({
      type: 'QUARTER',
      code: 'Q' + q,
      label: 'Q' + q,
      key: 'q' + q,
      isAnnual: false
    })
  }
  // 年度
  periods.push({
    type: 'ANNUAL',
    code: 'ANNUAL',
    label: '全年',
    key: 'year',
    isAnnual: true
  })
  return periods
}

export default {
  name: 'OrderForecastReport',
  props: {
    isProfit: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      apiData: null,
      dynamicPeriods: getDynamicPeriods()
    }
  },
  async created() {
    await this.fetchData()
  },
  watch: {
    isProfit() {
      this.fetchData()
    }
  },
  computed: {
    displayPeriods() {
      return this.dynamicPeriods
    },
    updateDate() {
      var d = new Date()
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
    title() { return '卡奥斯在手订单全年预测 — ' + (this.isProfit ? '利润' : '收入') },
    subtitle() { return this.isProfit ? '基于收入预测、项目毛利、交付成本及费用归集，形成7月、Q3、Q4与全年利润滚动预测。' : '基于已签订单、执行进度、交付排期及赢单概率，形成当前月、季度与全年滚动预测。' },
    tableTitle() { return '链群' + (this.isProfit ? '利润' : '收入') + '预测明细' },
    rows() {
      return this.apiRows
    },
    totalRow() {
      return this.apiTotalRow
    },
    kpis() {
      return this.apiKpis
    },
    apiRows() {
      if (!this.apiData || !this.apiData.length) return []
      var groups = []
      var seen = {}
      this.apiData.forEach(function (d) {
        if (d.chain_group && d.chain_group !== '卡奥斯平台' && !seen[d.chain_group]) {
          seen[d.chain_group] = true
          groups.push(d.chain_group)
        }
      })
      var self = this
      return groups.map(function (g) { return self.mapApiRow(g) })
    },
    apiTotalRow() {
      if (!this.apiData) return {}
      return this.mapApiRow('卡奥斯平台')
    },
    apiKpis() {
      if (!this.apiData) return []
      var annual = this.apiData.find(function (d) {
        return d.chain_group === '卡奥斯平台' && d.period_type === 'ANNUAL'
      })
      if (!annual) return []
      var yoy = annual.yoy_growth_rate != null ? Number(annual.yoy_growth_rate) : null
      var kpiValueField = this.isProfit ? 'target_profit' : 'forecast_revenue'
      var forecastLabel = this.isProfit ? '全年预测利润' : '全年预测收入'
      return [
        { label: forecastLabel, value: this.num(annual[kpiValueField]) + '万', note: '在手订单滚动测算', tone: 'primary' },
        { label: '全年目标完成率', value: annual.completion_rate != null ? Number(annual.completion_rate).toFixed(1) + '%' : '—', note: '距目标仍有提升空间', tone: 'warning' },
        { label: '全年同比增幅', value: yoy != null ? yoy.toFixed(1) + '%' : '—', note: '对比2025年同期口径', tone: yoy != null ? (yoy >= 0 ? 'positive' : 'negative') : 'negative' },
        { label: '下半年贡献占比', value: annual.second_half_contribution_rate != null ? Number(annual.second_half_contribution_rate).toFixed(1) + '%' : '—', note: 'Q3与Q4预测合计', tone: 'primary' },
        { label: '重点风险链群', value: (annual.key_risk_group_count != null ? annual.key_risk_group_count : 0) + '个', note: '全年完成率低于90%', tone: 'negative' }
      ]
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        var year = new Date().getFullYear().toString()
        var url = this.isProfit
          ? 'https://bigdata.cosmoplat.com/prod-api/system/api/call/ceo/order-profit-forecast'
          : 'https://bigdata.cosmoplat.com/prod-api/system/api/call/ceo/order-income-forecast'
        var response = await axios.post(
          url,
          { secret: '88py6X', paramList: [{ paramName: 'year', paramValue: year }] },
          { headers: { 'Content-Type': 'application/json' }}
        )
        this.apiData = (response.data && response.data.data) || []
      } catch (error) {
        console.error('获取在手订单全年预测数据失败:', error)
        this.apiData = []
      } finally {
        this.loading = false
      }
    },
    findPeriodData(chainGroup, periodType, periodCode) {
      if (!this.apiData) return null
      return this.apiData.find(function (d) {
        return d.chain_group === chainGroup &&
          d.period_type === periodType &&
          d.period_code === periodCode
      })
    },
    mapApiRow(chainGroup) {
      var row = { name: chainGroup }
      var self = this
      var forecastField = this.isProfit ? 'forecast_profit' : 'forecast_revenue'
      this.displayPeriods.forEach(function (p) {
        var item = self.findPeriodData(chainGroup, p.type, p.code)
        if (item) {
          var rate = p.isAnnual ? item.guarantee_rate : item.completion_rate
          row[p.key] = [
            item[forecastField] != null ? Number(item[forecastField]) : null,
            rate != null ? Number(rate) : null,
            item.yoy_growth_rate != null ? Number(item.yoy_growth_rate) : null
          ]
        } else {
          row[p.key] = [null, null, null]
        }
      })
      return row
    },
    num(v) {
      if (v == null || v === '') return '—'
      var n = Number(v)
      if (isNaN(n)) return '—'
      return Math.round(n).toLocaleString('zh-CN')
    },
    toneColor(t) { var m = { primary: '#4a5ff3', positive: '#0aa66a', warning: '#d98213', negative: '#e54b3d' }; return m[t] || '#4a5ff3' },
    rateColor(val, kind) {
      if (kind === 'growth') return val >= 0 ? '#0aa66a' : '#e54b3d'
      if (val >= 100) return '#0aa66a'
      if (val >= 95) return '#4a5ff3'
      if (val >= 90) return '#d98213'
      return '#e54b3d'
    },
    requestAnalysis() {
      var t = this.totalRow
      if (!t || !t.year) return
      var unit = this.isProfit ? '利润' : '收入'
      var quarterPeriods = this.displayPeriods.filter(function (p) { return p.type === 'QUARTER' })
      var h2 = quarterPeriods.reduce(function (s, p) { return s + (t[p.key] && t[p.key][0] ? t[p.key][0] : 0) }, 0)
      var h2Share = t.year[0] ? h2 / t.year[0] * 100 : 0
      var getYearVal = function (r, idx) { return (r.year && r.year[idx] != null) ? r.year[idx] : 0 }
      var sorted = this.rows.slice().sort(function (a, b) { return getYearVal(a, 1) - getYearVal(b, 1) })
      var growthLeader = this.rows.slice().sort(function (a, b) { return getYearVal(b, 2) - getYearVal(a, 2) })[0]
      var riskRows = sorted.filter(function (r) { return getYearVal(r, 1) < 95 || getYearVal(r, 2) < 0 }).slice(0, 3)
      var risks = riskRows.map(function (r) {
        return r.name + '全年' + unit + '完成率为' + getYearVal(r, 1).toFixed(1) + '%，同比增幅为' +
          (getYearVal(r, 2) >= 0 ? '+' : '') + getYearVal(r, 2).toFixed(1) + '%，需重点校验订单兑现和项目质量。'
      })
      if (!risks.length) risks.push('当前各链群全年目标覆盖均处于可控区间，仍需持续关注Q4集中交付带来的预测波动。')
      window.parent.postMessage({
        type: 'cosmo:business-report-analysis',
        report: {
          key: 'order-forecast-' + (this.isProfit ? 'profit' : 'revenue'),
          title: '卡奥斯在手订单全年预测 — ' + unit,
          summary: [
            '全年预测' + unit + '为' + this.num(t.year[0]) + '万元，目标完成率' + getYearVal(t, 1).toFixed(1) + '%，同比' + (getYearVal(t, 2) >= 0 ? '增长' : '下降') + Math.abs(getYearVal(t, 2)).toFixed(1) + '%。',
            '下半年合计预测' + this.num(h2) + '万元，占全年预测的' + h2Share.toFixed(1) + '%，下半年是目标兑现的主要周期。',
            growthLeader ? growthLeader.name + '全年同比增幅最高，为+' + getYearVal(growthLeader, 2).toFixed(1) + '%，是当前最主要的增长支撑。' : ''
          ],
          risks: risks,
          recommendations: this.isProfit ? [
            '对完成率低于95%的链群逐项目复核报价、交付变更、采购成本和费用归集，建立收入到利润的穿透校验。',
            '优先保障高利润增长链群的关键交付资源，避免收入确认完成但利润兑现滞后。',
            '按周更新Q4项目毛利预测，对负增长链群设置毛利底线和专项纠偏责任人。'
          ] : [
            '对完成率低于95%的链群建立订单、交付、验收和开票四级清单，按周校正预测。',
            '提前锁定Q4大额项目验收节点和客户预算，降低收入集中在年末确认的波动风险。',
            '向高增长链群倾斜交付与售前资源，同时补充低完成链群的高确定性商机储备。'
          ],
          data: { reportType: this.isProfit ? 'profit' : 'revenue', total: t, rows: this.rows }
        }
      }, window.location.origin)
    }
  }
}
</script>

<style lang="scss">
.rpt-order-forecast {
  height: 100%; min-height: 0; padding: 16px 20px;
  background: #eef3f9; overflow-y: auto;
  font-family: "PingFang SC", "HarmonyOS Sans SC", "Microsoft YaHei", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
  scrollbar-width: thin; scrollbar-color: #fff transparent;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #fff; border-radius: 2px; }

  .report-loading { display: flex; align-items: center; justify-content: center; height: 100%; min-height: 200px; font-size: 14px; color: #64748b; gap: 8px; }

  h1 { margin: 0 0 6px; font-size: 24px; line-height: 1.25; letter-spacing: -.02em; color: #0f172a; font-weight: 800; }

  .report-shell { display: flex; height: 100%; max-width: 1600px; margin: 0 auto; flex-direction: column; }

  .report-header { position: relative; overflow: hidden; padding: 18px 22px; border: 1px solid rgba(148,163,184,.28); border-radius: 15px; background: linear-gradient(135deg,#ffffff,#f8fafc); box-shadow: 0 16px 38px rgba(15,23,42,.08); flex-shrink: 0; margin-bottom: 12px;
    &::after { content: ""; position: absolute; right: -80px; top: -100px; width: 260px; height: 260px; border-radius: 50%; background: radial-gradient(circle,rgba(74,95,243,.18),rgba(74,95,243,0) 68%); pointer-events: none; }
  }
  .header-row { position: relative; z-index: 1; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
  .subtitle { margin: 0; color: #64748b; font-size: 13px; line-height: 1.5; }
  .header-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
  .report-badge { height: 34px; display: inline-flex; align-items: center; gap: 7px; padding: 0 13px; border-radius: 9px; border: 1px solid rgba(148,163,184,.28); background: #fff; color: #64748b; font-size: 12px; font-weight: 700; }
  .analysis-button { height: 34px; display: inline-flex; align-items: center; gap: 7px; padding: 0 14px; border-radius: 9px; border: none; background: linear-gradient(135deg,#4a5ff3,#2d7ff9); color: #fff; font-size: 12px; font-weight: 700; cursor: pointer; box-shadow: 0 7px 16px rgba(74,95,243,.2);
    &:hover { filter: brightness(1.08); }
  }

  .kpi-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 8px 0; flex-shrink: 0; }
  .kpi-card { position: relative; overflow: hidden; padding: 10px 12px; border: 1px solid rgba(148,163,184,.28); border-radius: 12px; background: #fff; box-shadow: 0 6px 16px rgba(15,23,42,.045);
    &::after { content: ""; position: absolute; right: -24px; bottom: -34px; width: 86px; height: 86px; border-radius: 50%; background: radial-gradient(circle,rgba(74,95,243,.1),transparent 70%); pointer-events: none; }
  }
  .kpi-label { font-size: 12px; color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .kpi-value { margin-top: 4px; font-size: 20px; font-weight: 850; letter-spacing: -.02em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .kpi-note { margin-top: 2px; font-size: 10px; color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .table-card { display: flex; min-height: 0; flex: 1; overflow: hidden; border: 1px solid rgba(148,163,184,.28); border-radius: 15px; background: #fff; box-shadow: 0 16px 38px rgba(15,23,42,.08); flex-direction: column; }
  .table-card-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding: 12px 16px; border-bottom: 1px solid rgba(148,163,184,.28); flex-shrink: 0; }
  .section-title { display: flex; align-items: center; gap: 9px; font-size: 14px; font-weight: 800; color: #0f172a; }
  .section-note { margin-top: 4px; font-size: 11px; color: #64748b; }
  .legend { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; font-size: 11px; color: #64748b; flex-shrink: 0;
    span { display: inline-flex; align-items: center; gap: 5px; }
  }
  .legend-dot { width: 8px; height: 8px; border-radius: 50%; }

  .table-scroll { min-height: 0; flex: 1; overflow: auto;
    table { width: 100%; height: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; font-size: 11px; font-variant-numeric: tabular-nums; }
    thead {
      th { position: sticky; z-index: 4; color: #fff; text-align: center; font-weight: 800; padding: 7px 5px; border-right: 1px solid rgba(148,163,184,.28); border-bottom: 1px solid rgba(148,163,184,.28); white-space: nowrap; background: #0d5fac; }
      tr:first-child th { top: 0; font-size: 12px; }
      tr:nth-child(2) th { top: 32px; background: #126fbe; font-size: 10px; }
      th:first-child { z-index: 6; }
    }
    .col-name { width: 10%; }
    th:first-child, td:first-child { position: sticky; left: 0; text-align: left; }
    tbody {
      td { padding: 7px 6px; border-right: 1px solid rgba(148,163,184,.28); border-bottom: 1px solid rgba(148,163,184,.28); text-align: right; white-space: nowrap; }
      td:first-child { z-index: 2; background: #fff; font-weight: 700; }
      tr:hover td { background: #f3f7ff; }
      tr:hover td:first-child { background: #f3f7ff; }
      tr.total-row td { font-weight: 850; background: linear-gradient(90deg,rgba(74,95,243,.14),rgba(45,127,249,.055)); }
      tr.total-row td:first-child { background: linear-gradient(90deg,rgba(74,95,243,.2),rgba(74,95,243,.12)); color: #4a5ff3; }
      tr:last-child td { border-bottom: 0; }
    }
    th:last-child, td:last-child { border-right: 0; }
  }
  .forecast-cell { font-weight: 750; }
  .rate-cell { position: relative; overflow: hidden; }
  .rate-fill { position: absolute; inset: 3px auto 3px 3px; border-radius: 4px; opacity: .11; pointer-events: none; }
  .rate-text { position: relative; z-index: 1; font-weight: 750; }

  .report-foot { display: flex; justify-content: space-between; gap: 12px; padding: 8px 14px; border-top: 1px solid rgba(148,163,184,.28); font-size: 10px; color: #64748b; line-height: 1.5; flex-shrink: 0; }
}
</style>
